import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Grid,
  Link,
  Divider,
  Image,
  Typography,
  VStack,
} from '@tq/ui-components';
import { tqc } from '@tq/ui-components/dist/system';
import { transparentize } from '@tq/ui-components/dist/theme-tools';

import logo from 'assets/white_logo.svg';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { Copyright } from './Copyright';
import { NIFooterAuthorities } from './authorities';
import { TERMS_CONDITIONS_URL } from 'config';

export const NiAppFooter: React.FC = () => {
  const { t } = useTranslation('footer');
  const isMobile = useIsMobile();

  const usefulLinksItem = {
    [t('useful_links', 'Useful links')]: {
      [t('development_management_practice_notes', 'Development Management Practice Notes')]:
        'https://www.infrastructure-ni.gov.uk/publications/development-management-practice-notes',
      [t('contact_us', 'Contact us')]:
        'https://www.nidirect.gov.uk/contacts/planning-offices-ni',
      [t('terms_and_conditions', 'Terms and Conditions')]: TERMS_CONDITIONS_URL,
    },
  };

  const footerMenu = [usefulLinksItem];

  return (
    <tqc.div as='footer' className='tqc-footer'>
      <Box
        pt={{ base: '2.25rem', md: '2rem', lg: '1.75rem' }}
        pb={{ base: '1.75rem', md: '2.75rem' }}
        backgroundColor='blue.700'
      >
        <Container>
          <Link
            href='/'
            target='_blank'
            title='NI'
            sx={{
              border: '1px solid',
              borderColor: 'transparent',
              display: 'inline-block',
              _focus: {
                border: '1px solid',
                borderColor: 'blue.300',
                borderRadius: '4px',
                boxShadow: '0 0 3px 2px #2182EB',
              },
            }}
          >
            <Image
              src={logo}
              w={{ base: '11rem', md: '20.5rem', lg: '22rem' }}
              h={{ base: '2.5rem', md: '4.5rem' }}
              alt={t('logo_alt', 'Consultee hub logo')}
            />
          </Link>
          <Divider
            sx={{
              borderColor: transparentize('grey.400', isMobile ? 0.8 : 0.2),
              mb: '2rem',
            }}
          />
          <>
            {footerMenu.map((subMenu, id) => {
              const subMenuTitle = Object.keys(subMenu)[0];
              const subMenuItems = Object.values(subMenu)[0];
              return (
                <VStack key={id} alignItems='left'>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      letterSpacing: '-0.013em',
                      fontSize: '1rem',
                      lineHeight: '1.5rem',
                      color: 'white',
                      mb: '0.75rem',
                    }}
                  >
                    {subMenuTitle}
                  </Typography>
                  <Grid
                    gridAutoFlow={{ base: 'row', md: 'column' }}
                    gridTemplateColumns={{ base: 'auto', md: 'repeat(3, max-content)' }}
                    gridGap={{ sm: '0', md: '3.125rem', mdlg: '6.25rem' }}
                    alignItems='center'
                    mb={3}
                  >
                    {Object.entries(subMenuItems).map(([key, value], itemIndex) => (
                      <Link
                        key={itemIndex}
                        href={value}
                        target='_blank'
                        title={key}
                        sx={{
                          display: 'inline-block',
                          mb: '.75rem',
                          letterSpacing: '-0.2px',
                          fontWeight: 'normal',
                          color: 'blue.300',
                          border: '1px solid',
                          borderColor: 'transparent',
                          _hover: {
                            color: 'white',
                            textDecoration: 'underline',
                          },
                          _focus: {
                            py: 0,
                            borderRadius: '.25rem',
                            borderColor: 'blue.300',
                            boxShadow: `0 0 3px 2px #2182EB`,
                          },
                          _active: {
                            color: 'blue.300',
                          },
                        }}
                      >
                        {key}
                      </Link>
                    ))}
                  </Grid>
                </VStack>
              );
            })}
          </>
        </Container>
      </Box>

      <Box
        sx={{
          pt: { base: '3rem', lg: '3.5rem' },
          pb: { base: '2.5rem', md: '4.625rem', lg: '4rem' },
          background: '#eff8ff',
        }}
      >
        <Container>
          <Typography
            sx={{
              color: 'grey.970',
              mb: { base: '2rem', md: '3rem', lg: '2rem' },
              fontWeight: 700,
              letterSpacing: '-0.013em',
            }}
          >
            {t('participating_authorities', 'Participating authorities')}
          </Typography>
          <NIFooterAuthorities />
        </Container>
      </Box>

      <Copyright />
    </tqc.div>
  );
};
